import type { RaceConfig } from '../../types/config.ts';
import type { NormalizedResult } from '../../types/results.ts';
import type { ProviderState as PState } from '../../types/ui.ts';
import type { Reconciliation } from '../../lib/reconcile.ts';
import { candidateById, candidateFullName } from '../../lib/candidates.ts';
import { candidateColor } from '../../lib/colors.ts';
import { marginSentence } from '../../lib/a11y.ts';
import { fmtInt, fmtPercent } from '../../lib/format.ts';
import { CandidateToken } from '../common/CandidateToken.tsx';
import { Provenance } from '../common/Provenance.tsx';
import { SourceState } from '../common/SourceState.tsx';

type VoteStates = Record<string, PState<NormalizedResult>>;

function CallStatusLine({ race, result }: { race: RaceConfig; result: NormalizedResult }) {
  if (result.callStatus === 'projected' && result.callFor) {
    return (
      <p className="callstatus callstatus--projected">
        Projected winner: <strong>{candidateFullName(race, result.callFor)}</strong>{' '}
        <span className="dim">· per {result.source}</span>
      </p>
    );
  }
  if (result.callStatus === 'leaning' && result.callFor) {
    return (
      <p className="callstatus callstatus--leaning">
        Leaning {candidateFullName(race, result.callFor)} <span className="dim">· per {result.source}</span>
      </p>
    );
  }
  return <p className="callstatus dim">Not called · per {result.source}</p>;
}

/** The loudest thing on the page: leader + margin + % reporting. The aria-live
 *  region announces updates without moving focus; the big figure is aria-hidden
 *  (announced via the summary sentence instead). */
export function HeadlineResult({
  race,
  reconciliation,
  voteStates,
  resolved,
}: {
  race: RaceConfig;
  reconciliation: Reconciliation;
  voteStates: VoteStates;
  resolved: 'dark' | 'light';
}) {
  const { primary, primaryMargin } = reconciliation;

  if (!primary || !primaryMargin) {
    const states = Object.values(voteStates);
    const loading = states.some((s) => s.status === 'loading' || s.status === 'idle');
    const firstErr = states.find((s) => s.status === 'error');
    return <SourceState label="Live count" kind={loading ? null : firstErr?.errorKind ?? null} loading={loading} />;
  }

  const primaryState = voteStates[primary.sourceId];
  const leader = candidateById(race, primaryMargin.leaderId);
  const color = leader ? candidateColor(leader, resolved) : 'var(--text-mid)';

  return (
    <div className="headline">
      <p className="visually-hidden" aria-live="polite">
        {marginSentence(race, primaryMargin)}
      </p>

      <div className="headline__top">
        {leader ? (
          <CandidateToken candidate={leader} color={color} size={18} />
        ) : (
          <span className="dim">No leader yet</span>
        )}
        <span className="headline__verb dim">{primaryMargin.marginPct != null ? 'leads by' : 'leads'}</span>
      </div>

      <p className="headline__margin" aria-hidden="true">
        <span className="headline__margin-num tabular" style={{ color }}>
          {primaryMargin.marginPct != null ? primaryMargin.marginPct.toFixed(1) : '—'}
        </span>
        <span className="headline__margin-unit">pts</span>
      </p>

      <p className="headline__meta">
        <span className="tabular">{fmtPercent(primaryMargin.reportingPercent)}</span>{' '}
        <span className="dim">reporting</span>
        {primaryMargin.marginVotes != null ? (
          <>
            {' '}
            <span className="dim">·</span> <span className="tabular">{fmtInt(primaryMargin.marginVotes)}</span>{' '}
            <span className="dim">vote margin</span>
          </>
        ) : null}
        {primary.estimatedVotesRemaining != null ? (
          <>
            {' '}
            <span className="dim">·</span> <span className="tabular">{fmtInt(primary.estimatedVotesRemaining)}</span>{' '}
            <span className="dim">est. remaining</span>
          </>
        ) : null}
      </p>

      <CallStatusLine race={race} result={primary} />
      <Provenance
        source={primary.source}
        fetchedAt={primary.fetchedAt}
        isStale={primaryState?.isStale}
        entry={primary.entry}
      />
    </div>
  );
}
